import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { Decision, DecisionAudit, PatternBased, RuleAction, RuleBased } from '@kkp-fraud/shared';
import { api } from '../api';
import { RANGE_CEILING } from '../decisionsRange';
import { useShell } from '../components/AppLayout';
import { CardLink, RuleLinks } from '../components/Links';
import { When } from '../components/When';
import { ActionChip, DecisionChip } from '../components/Chips';
import { PageHead, SubTabs, Tip } from '../components/PageHead';
import { downloadText, toCsv, type CsvCell } from '../csv';
import { dateTime, ms, num, parseThaiDay, shortCard, thaiDay, thaiMidnight } from '../format';
import { LoadError } from '../components/LoadError';
import { t, useLocale } from '../i18n';

/**
 * Report — the page filters, three runnable reports over the decision audit, CSV per
 * report and one Print / Save as PDF for the page.
 *
 * The period is asked of the SERVER: `api.decisionsRange` pages through every decision whose
 * event time is in `[from, until)` (up to `RANGE_CEILING` rows, and the page says so when it
 * bites), and a new period is a new fetch. The decision / rule / version filters then narrow
 * that one list in the browser, and every number on the page is computed from the result.
 */
const DECISIONS: readonly Decision[] = ['APPROVE', 'APPROVE_NOTIFY', 'STEP_UP', 'DECLINE'];

type Period = 'today' | 'month' | 'last-month' | 'custom';

/**
 * `YYYY-MM-DD` of the THAI calendar day an instant falls on — what a date input holds and a
 * file name carries. Periods are Bangkok days whatever the browser's own zone (QA-M-04): every
 * time on the page is Thai time, so "today" must be the Thai today too.
 */
export function ymd(d: Date): string {
  return thaiDay(d);
}

/** A Thai calendar day, normalised (`{2026, 10, 0}` → 2026-09-30). */
interface Day {
  y: number;
  m: number;
  d: number;
}

function day(y: number, m: number, d: number): Day {
  const n = new Date(Date.UTC(y, m - 1, d));
  return { y: n.getUTCFullYear(), m: n.getUTCMonth() + 1, d: n.getUTCDate() };
}

const dayText = ({ y, m, d }: Day): string => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export interface Range {
  /** Inclusive start: 00:00 Bangkok on the first day. */
  from: Date;
  /** Exclusive end: 00:00 Bangkok on the day after the last. */
  until: Date;
  /** The first and last day (`YYYY-MM-DD`, Thai calendar), inclusive — what the chip and the file name say. */
  first: string;
  last: string;
}

export function rangeFor(period: Period, customFrom: string, customTo: string, now = new Date()): Range | null {
  const today = parseThaiDay(thaiDay(now))!;
  let first: Day;
  let last: Day;
  if (period === 'today') {
    first = today;
    last = today;
  } else if (period === 'month') {
    first = day(today.y, today.m, 1);
    last = today;
  } else if (period === 'last-month') {
    first = day(today.y, today.m - 1, 1);
    last = day(today.y, today.m, 0);
  } else {
    const a = parseThaiDay(customFrom);
    const b = parseThaiDay(customTo);
    if (!a || !b || dayText(b) < dayText(a)) return null;
    first = a;
    last = b;
  }
  return {
    from: thaiMidnight(first.y, first.m, first.d),
    until: thaiMidnight(last.y, last.m, last.d + 1),
    first: dayText(first),
    last: dayText(last),
  };
}

/** Nearest-rank percentile; `null` for an empty list. */
function percentile(values: readonly number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.max(0, Math.ceil((p / 100) * sorted.length) - 1)]!;
}

const pct = (n: number, of: number): string => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '—');

/** The rule ids a decision fired: `reasons`, plus any hit it names that reasons does not. */
/** Rules that fired — FAIL_SAFE is the engine's fail-safe marker (reported as degraded), not a rule. */
const firedBy = (d: DecisionAudit, primary?: string): string[] => {
  // 2c rule sets: a hit of another set is `<set id>/<rule id>` — two sets may both have an RB-01.
  const hits = d.hits ?? [];
  if (!hits.some((h) => h.rule_set_id)) {
    return [...new Set([...(d.reasons ?? []), ...hits.map((h) => h.rule_id)])].filter((id) => id !== 'FAIL_SAFE');
  }
  const keys = hits.map((h) => (h.rule_set_id && h.rule_set_id !== primary ? `${h.rule_set_id}/${h.rule_id}` : h.rule_id));
  const hitIds = new Set(hits.map((h) => h.rule_id));
  return [...new Set([...(d.reasons ?? []).filter((r) => !hitIds.has(r)), ...keys])].filter((id) => id !== 'FAIL_SAFE');
};

/** One report's column: the header comes from the catalogue, the cell and the CSV value from the row. */
interface Column<R> {
  label: string;
  cell: (r: R) => ReactNode;
  csv: (r: R) => CsvCell;
  className?: string;
}

interface Report<R> {
  id: 'summary' | 'rule-hits' | 'decisions';
  /** A short label/value table: kept to ~720 px so each value sits near its label (UX review P2-14). */
  compact?: boolean;
  /** Rows drawn at once; "Show more" adds as many again. Absent → every row. The CSV is always every row. */
  pageSize?: number;
  title: string;
  question: string;
  audience: string;
  columns: Column<R>[];
  rows: R[];
  key: (r: R, i: number) => string;
}

/** Report's decision list draws this many rows, then "Show more" (UX review P1-2: 1,753 rows made a 71,526px page). */
export const REPORT_PAGE = 100;

function ReportSection<R>({ report, onExport, disabled }: { report: Report<R>; onExport: () => void; disabled: boolean }) {
  const page = report.pageSize ?? Number.POSITIVE_INFINITY;
  const [limit, setLimit] = useState(page);
  // A new result (period, filters) starts at the first page again.
  useEffect(() => setLimit(page), [report.rows, page]);
  // Printing is the whole report, as before paging: every row is drawn for the print, then paged again.
  useEffect(() => {
    if (!Number.isFinite(page)) return;
    const all = () => flushSync(() => setLimit(Number.POSITIVE_INFINITY));
    const back = () => setLimit(page);
    window.addEventListener('beforeprint', all);
    window.addEventListener('afterprint', back);
    return () => {
      window.removeEventListener('beforeprint', all);
      window.removeEventListener('afterprint', back);
    };
  }, [page]);
  const rows = report.rows.length > limit ? report.rows.slice(0, limit) : report.rows;
  return (
    <section className="card report-section" data-testid={`report-${report.id}`}>
      <header className="card-head">
        <h2>{report.title}</h2>
        <Tip>{report.question}</Tip>
        <span className="chip mute">{report.audience}</span>
        <span className="spacer" />
        <button type="button" className="btn small" data-testid={`report-${report.id}-csv`} disabled={disabled} onClick={onExport}>
          {t('report.export.label', { format: 'CSV' })}
        </button>
      </header>
      <div className={report.compact ? 'tbl-wrap compact' : 'tbl-wrap'}>
        <table>
          <thead>
            <tr>
              {report.columns.map((c) => (
                <th key={c.label} className={c.className}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 && (
              <tr>
                <td colSpan={report.columns.length} className="empty" role="status">
                  {t('report.empty')}
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={report.key(r, i)}>
                {report.columns.map((c) => (
                  <td key={c.label} className={c.className}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length < report.rows.length && (
        <div className="row-wrap no-print" style={{ marginTop: 10 }} data-testid={`report-${report.id}-more-row`}>
          <span className="small muted" data-testid={`report-${report.id}-shown`}>
            {t('report.shown.label', { shown: num(rows.length), n: num(report.rows.length) })}
          </span>
          <button type="button" className="btn small" data-testid={`report-${report.id}-more`} onClick={() => setLimit((n) => n + page)}>
            {t('report.more.action')}
          </button>
        </div>
      )}
    </section>
  );
}

interface SummaryRow {
  id: string;
  metric: ReactNode;
  metricCsv: string;
  value: string;
  valueCsv: CsvCell;
  share: string;
  /** The share as 0–1 for its proportion bar; `null` draws none (the total, the latencies). */
  ratio: number | null;
}

/** A share with its proportion bar, as on the Dashboard (UX review P2-14). The bar is decoration: the text says it. */
function ShareCell({ text, ratio }: { text: string; ratio: number | null }) {
  return (
    <span className="share">
      {ratio !== null && (
        <span className="bar" aria-hidden="true">
          <i style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }} />
        </span>
      )}
      <span>{text}</span>
    </span>
  );
}

interface HitRow {
  id: string;
  name: string;
  actions: RuleAction[];
  hits: number;
  by: Record<Decision, number>;
}

function actionsOf(rule: RuleBased | PatternBased): RuleAction[] {
  const rows = rule.branches;
  return [...new Set(rows && rows.length > 0 ? rows.map((b) => b.action) : [rule.action])];
}

export function ReportScreen() {
  const { state, settings } = useShell();
  // The reports below are memos that call t(): the language is one of their inputs (QA-M-01).
  const locale = useLocale();
  const draft = state.draft;
  const allRules = useMemo(() => (draft ? [...draft.ruleBased, ...draft.patternBased] : []), [draft]);
  // 2c rule sets: the primary's rules are named by id alone; another set's as `<set id>/<rule id>`.
  const primarySet = state.loaded?.ruleSetId;

  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState(() => ymd(new Date()));
  const [customTo, setCustomTo] = useState(() => ymd(new Date()));
  const [decision, setDecision] = useState<'all' | Decision>('all');
  const [ruleId, setRuleId] = useState('all');
  const [version, setVersion] = useState('all');

  /** The decisions of the period on screen — null until its first fetch lands. */
  const [decisions, setDecisions] = useState<DecisionAudit[] | null>(null);
  /** The period holds more than `RANGE_CEILING` rows: what is shown is the newest part of it. */
  const [capped, setCapped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Rows received so far for the fetch in flight. */
  const [progress, setProgress] = useState(0);
  /** One report at a time — each is its own tab; Print prints the one in front. */
  const [tab, setTab] = useState<'summary' | 'rule-hits' | 'decisions'>('summary');

  const range = useMemo(() => rangeFor(period, customFrom, customTo), [period, customFrom, customTo]);
  const fromMs = range?.from.getTime() ?? null;
  const untilMs = range?.until.getTime() ?? null;

  /**
   * Which fetch is current. Changing the period (or typing a custom date, one valid value
   * per keystroke) starts a new fetch; an older one stops paging at its next page and its
   * answer is dropped, so a slow "last month" can never land on top of "today".
   */
  const fetchSeq = useRef(0);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const seq = ++fetchSeq.current;
    if (fromMs === null || untilMs === null) {
      // An invalid custom range has no result: the last period's rows must not stay on screen
      // (or go to the printer) under the "pick a valid range" message (QA-M-07).
      setLoading(false);
      setDecisions(null);
      setCapped(false);
      setProgress(0);
      setError(null);
      return;
    }
    const current = () => seq === fetchSeq.current;
    setLoading(true);
    setDecisions(null);
    setCapped(false);
    setProgress(0);
    setError(null);
    api
      .decisionsRange(settings, { from: new Date(fromMs).toISOString(), to: new Date(untilMs).toISOString() }, ({ loaded }) => {
        if (!current()) return false;
        setProgress(loaded);
        return true;
      })
      .then(
        (r) => {
          if (!current()) return;
          setDecisions(r.items);
          setCapped(r.capped);
        },
        (e: unknown) => {
          if (!current()) return;
          // The technical detail; the sentence is t() at render time, so it follows the language.
          setError((e as Error)?.message ?? String(e));
        },
      )
      .finally(() => {
        if (current()) setLoading(false);
      });
  }, [settings.baseUrl, fromMs, untilMs, tick]);

  const versions = useMemo(() => {
    const set = new Set<string>((decisions ?? []).map((d) => d.ruleSetVersion).filter(Boolean));
    if (state.loaded) set.add(state.loaded.version);
    return [...set].sort().reverse();
  }, [decisions, state.loaded]);

  const rows = useMemo(() => {
    if (!decisions || !range) return [];
    // The server already answered for this period; the decision / rule / version filters
    // are the browser's.
    return decisions.filter((d) => {
      if (decision !== 'all' && d.decision !== decision) return false;
      if (ruleId !== 'all' && !firedBy(d, primarySet).includes(ruleId)) return false;
      if (version !== 'all' && d.ruleSetVersion !== version) return false;
      return true;
    });
  }, [decisions, range, decision, ruleId, version, primarySet]);

  const total = rows.length;

  // ---- decision summary ----
  const summary: Report<SummaryRow> = useMemo(() => {
    const count = (d: Decision) => rows.filter((r) => r.decision === d).length;
    const degraded = rows.filter((r) => r.degraded).length;
    const latencies = rows.map((r) => r.engineLatencyMs).filter((n) => Number.isFinite(n));
    const p50 = percentile(latencies, 50);
    const p95 = percentile(latencies, 95);
    const list: SummaryRow[] = [
      ...DECISIONS.map((d) => {
        const n = count(d);
        return { id: d, metric: <DecisionChip decision={d} />, metricCsv: d, value: num(n), valueCsv: n, share: pct(n, total), ratio: total > 0 ? n / total : null };
      }),
      {
        id: 'total',
        metric: <strong>{t('report.summary.total')}</strong>,
        metricCsv: t('report.summary.total'),
        value: num(total),
        valueCsv: total,
        share: pct(total, total),
        ratio: null,
      },
      {
        id: 'degraded',
        metric: t('report.summary.degraded'),
        metricCsv: t('report.summary.degraded'),
        value: num(degraded),
        valueCsv: degraded,
        share: pct(degraded, total),
        ratio: total > 0 ? degraded / total : null,
      },
      { id: 'p50', metric: t('report.summary.p50'), metricCsv: t('report.summary.p50'), value: ms(p50), valueCsv: p50, share: '', ratio: null },
      { id: 'p95', metric: t('report.summary.p95'), metricCsv: t('report.summary.p95'), value: ms(p95), valueCsv: p95, share: '', ratio: null },
    ];
    return {
      id: 'summary',
      compact: true,
      title: t('report.summary.title'),
      question: t('report.summary.question'),
      audience: t('report.summary.audience'),
      rows: total === 0 ? [] : list,
      key: (r) => r.id,
      columns: [
        { label: t('report.col.metric'), cell: (r) => r.metric, csv: (r) => r.metricCsv },
        { label: t('report.col.value'), cell: (r) => r.value, csv: (r) => r.valueCsv, className: 'right mono' },
        { label: t('report.col.share'), cell: (r) => <ShareCell text={r.share} ratio={r.ratio} />, csv: (r) => r.share, className: 'right mono' },
      ],
    };
  }, [rows, total, locale]);

  // ---- rule hits ----
  const ruleHits: Report<HitRow> = useMemo(() => {
    const byId = new Map<string, HitRow>();
    const zero = (): Record<Decision, number> => ({ APPROVE: 0, APPROVE_NOTIFY: 0, STEP_UP: 0, DECLINE: 0 });
    for (const r of allRules) byId.set(r.id, { id: r.id, name: r.name, actions: actionsOf(r), hits: 0, by: zero() });
    for (const d of rows) {
      for (const id of firedBy(d, primarySet)) {
        const row = byId.get(id) ?? { id, name: '', actions: [], hits: 0, by: zero() };
        row.hits += 1;
        row.by[d.decision] = (row.by[d.decision] ?? 0) + 1;
        byId.set(id, row);
      }
    }
    const list = [...byId.values()]
      .filter((r) => ruleId === 'all' || r.id === ruleId)
      .sort((a, b) => b.hits - a.hits || a.id.localeCompare(b.id));
    return {
      id: 'rule-hits',
      title: t('report.hits.title'),
      question: t('report.hits.question'),
      audience: t('report.hits.audience'),
      rows: total === 0 ? [] : list,
      key: (r) => r.id,
      columns: [
        { label: t('col.rule.label'), cell: (r) => <RuleLinks ids={[r.id]} />, csv: (r) => r.id, className: 'mono nowrap' },
        { label: t('col.rule_name.label'), cell: (r) => r.name || <span className="muted">—</span>, csv: (r) => r.name },
        {
          label: t('col.action.label'),
          cell: (r) => (
            <span className="row-wrap">
              {r.actions.map((a) => (
                <ActionChip key={a} action={a} />
              ))}
            </span>
          ),
          csv: (r) => r.actions.join(' '),
        },
        { label: t('col.hits.label'), cell: (r) => num(r.hits), csv: (r) => r.hits, className: 'right mono' },
        { label: t('report.col.share_of_decisions'), cell: (r) => pct(r.hits, total), csv: (r) => pct(r.hits, total), className: 'right mono' },
        ...DECISIONS.map((d): Column<HitRow> => ({ label: d, cell: (r) => num(r.by[d]), csv: (r) => r.by[d], className: 'right mono' })),
      ],
    };
  }, [rows, total, allRules, ruleId, primarySet, locale]);

  // ---- decisions ----
  const list: Report<DecisionAudit> = useMemo(
    () => ({
      id: 'decisions',
      title: t('report.decisions.title'),
      question: t('report.decisions.question'),
      audience: t('report.decisions.audience'),
      rows,
      pageSize: REPORT_PAGE,
      key: (r, i) => `${r.id}-${i}`,
      columns: [
        // One zone for screen and file (QA-M-09): Thai time, the header says so, the CSV cell is what the screen shows.
        { label: t('report.col.time_bangkok'), cell: (r) => <When iso={r.eventTimeUtc} />, csv: (r) => dateTime(r.eventTimeUtc), className: 'small nowrap' },
        { label: t('report.col.txn'), cell: (r) => r.transactionId, csv: (r) => r.transactionId, className: 'mono small' },
        {
          label: t('report.col.card'),
          // R9 cases — the card's decisions and cases in Investigate (read-only: opening a case happens there).
          // UX review P2-13: the one short form, full id in the tooltip and the link, a copy button.
          cell: (r) => <CardLink cardId={r.cardId} testid={`report-investigate-${r.id}`} />,
          csv: (r) => shortCard(r.cardId),
          className: 'mono small',
        },
        { label: t('decisions.col.amount'), cell: (r) => num(r.amountThb, 2), csv: (r) => r.amountThb, className: 'right mono' },
        { label: t('report.col.network'), cell: (r) => r.network, csv: (r) => r.network, className: 'small' },
        { label: 'MCC', cell: (r) => r.mcc ?? '—', csv: (r) => r.mcc ?? '', className: 'mono small' },
        { label: t('col.decision.label'), cell: (r) => <DecisionChip decision={r.decision} />, csv: (r) => r.decision },
        {
          label: t('report.col.rules'),
          // 2c rule sets: another set's rule is `<set>/<rule>` (RuleLinks opens it in that set)
          cell: (r) => <RuleLinks ids={firedBy(r, primarySet)} />,
          csv: (r) => firedBy(r, primarySet).join(', '),
          className: 'mono small',
        },
        { label: t('report.col.latency'), cell: (r) => ms(r.engineLatencyMs), csv: (r) => r.engineLatencyMs, className: 'right mono' },
      ],
    }),
    [rows, primarySet, locale],
  );

  const exportCsv = <R,>(report: Report<R>) => {
    if (!range) return;
    const csv = toCsv(
      report.columns.map((c) => c.label),
      report.rows.map((r) => report.columns.map((c) => c.csv(r))),
    );
    downloadText(`kkp-fraud-${report.id}-${range.first}_${range.last}.csv`, csv);
  };

  const periodLabel: Record<Period, string> = {
    today: t('report.period.today.short'),
    month: t('report.period.this_month.short'),
    'last-month': t('report.period.last_month.short'),
    custom: t('report.period.custom.short'),
  };

  return (
    <>
      <PageHead title={t('screen.report.title')} lead={t('screen.report.console.lead')} />

      {/* What was printed: the filters in force, values only. Hidden on screen. */}
      <div className="print-only" data-testid="report-print-filters">
        {t('report.filter.period.short')}: {periodLabel[period]} ({range ? `${range.first} – ${range.last}` : '—'}) ·{' '}
        {t('report.filter.decision.short')}: {decision === 'all' ? t('report.decision.all') : decision} · {t('report.filter.rule.short')}:{' '}
        {ruleId === 'all' ? t('report.rule.all') : ruleId} · {t('report.filter.version.short')}: {version === 'all' ? t('report.version.all') : version}
      </div>

      <section className="card no-print">
        <header className="card-head">
          <h2>{t('report.filter.title')}</h2>
          {decisions && range && (
            <span className="chip mute" data-testid="report-coverage">
              {t(decisions.length === 1 ? 'report.range.chip.one' : 'report.range.chip.other', {
                n: num(decisions.length),
                from: range.first,
                to: range.last,
              })}
            </span>
          )}
          {decisions && capped && (
            <span className="chip warn" data-testid="report-capped">
              {t('report.capped.chip', { limit: num(RANGE_CEILING) })}
            </span>
          )}
          <Tip>{t('report.range.tip', { limit: num(RANGE_CEILING) })}</Tip>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={refresh} disabled={loading || !range}>
            {t('btn.refresh.label')}
          </button>
          <button
            type="button"
            className="btn small primary"
            data-testid="report-print"
            // Nothing to print without a valid period and its rows (QA-M-07).
            disabled={!range || !decisions}
            onClick={() => window.print()}
          >
            {t('report.print.action')}
          </button>
        </header>
        <div className="card-body">
          <div className="form-grid">
            <label className="field">
              {t('report.filter.period.short')}
              <select aria-label={t('report.period.a11y')} value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                <option value="today">{periodLabel.today}</option>
                <option value="month">{periodLabel.month}</option>
                <option value="last-month">{periodLabel['last-month']}</option>
                <option value="custom">{periodLabel.custom}</option>
              </select>
            </label>
            {period === 'custom' && (
              <>
                <label className="field">
                  {t('report.period.from')}
                  <input
                    type="date"
                    aria-label={t('report.period.from')}
                    className={range ? '' : 'invalid'}
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </label>
                <label className="field">
                  {t('report.period.to')}
                  <input
                    type="date"
                    aria-label={t('report.period.to')}
                    className={range ? '' : 'invalid'}
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </label>
              </>
            )}
            <label className="field">
              {t('report.filter.decision.short')}
              <select aria-label={t('report.decision.a11y')} value={decision} onChange={(e) => setDecision(e.target.value as 'all' | Decision)}>
                <option value="all">{t('report.decision.all')}</option>
                {DECISIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('report.filter.rule.short')}
              <select aria-label={t('report.rule.a11y')} value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
                <option value="all">{t('report.rule.all')}</option>
                {allRules.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id} — {r.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('report.filter.version.short')}
              <select aria-label={t('report.version.a11y')} value={version} onChange={(e) => setVersion(e.target.value)}>
                <option value="all">{t('report.version.all')}</option>
                {versions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </section>

      {loading && !decisions && (
        <div className="empty" role="status" aria-live="polite" data-testid="report-loading">
          {progress > 0 ? t('report.loading.progress', { n: num(progress) }) : t('btn.loading.label')}
        </div>
      )}
      {error && <LoadError message={t('report.error.load')} detail={error} testId="report-error" />}
      {!range && (
        <div className="banner error" role="status" data-testid="report-range-invalid">
          {t('report.range.invalid')}
        </div>
      )}

      {range && decisions && (
        <>
          <div className="no-print" style={{ marginTop: 22 }}>
            <SubTabs
              label={t('screen.report.title')}
              active={tab}
              onSelect={(id) => setTab(id as typeof tab)}
              tabs={[
                { id: 'summary', label: summary.title },
                { id: 'rule-hits', label: ruleHits.title },
                { id: 'decisions', label: list.title },
              ]}
            />
          </div>
          {tab === 'summary' && <ReportSection report={summary} disabled={!range} onExport={() => exportCsv(summary)} />}
          {tab === 'rule-hits' && <ReportSection report={ruleHits} disabled={!range} onExport={() => exportCsv(ruleHits)} />}
          {tab === 'decisions' && <ReportSection report={list} disabled={!range} onExport={() => exportCsv(list)} />}
        </>
      )}
    </>
  );
}
